import { getRoute } from "@/utils/routes";
import { Routes } from "../router";
import { NaturalPerson } from "../user/types";

export const getRoutesWithoutPadding = (session_uuid: string) => [
  getRoute(Routes.flow_step, { session_uuid, block: "documents", step: "kyc-session-iframe" })
];
export const cspWithoutActivity = { CHÔMEUR: 14, INACTIF: 18, MILITAIRE: 13, ETUDIANT: 15 };

type IdentityPersonKeys = "LastName" | "FirstName" | "Gender";
type IdentityBirthKeys = "countryOfBirth" | "DateOfBirth" | "StateOfBirth";
type IdentityNationalityKeys = "CountryOfCitizenship";
type IdentityActivityKeys = "CSPCode" | "ActivityCode";
type IdentitySourcesIncomeKeys = "sourcesIncome";
type IdentityMonthlyIncomeKeys = "minimumMonthlyIncome" | "maximumMonthlyIncome";
type PhoneKeys = "AliasMobile";
type IdentityResidenceKeys = "Address";
type IdentityTaxAddressKeys = "TaxAddress";

export interface RequiredConnectionKeysLv2 {
  "identity.source_income": IdentitySourcesIncomeKeys[];
  "identity.monthly_income": IdentityMonthlyIncomeKeys[];
}

export interface RequiredConnectionKeys extends RequiredConnectionKeysLv2 {
  "identity.person": IdentityPersonKeys[];
  "identity.birth": IdentityBirthKeys[];
  "identity.nationality": IdentityNationalityKeys[];
  "identity.activity": IdentityActivityKeys[];
  "phone.phone": PhoneKeys[];
  "identity.residence": IdentityResidenceKeys[];
  "identity.nif": IdentityTaxAddressKeys[];
}

export const REQUIRED_SUBSTEPS_AT_CONNECTION_LV2 = [
  "identity.monthly_income",
  "identity.source_income"
];

export const REQUIRED_SUBSTEPS_AT_CONNECTION = [
  ...REQUIRED_SUBSTEPS_AT_CONNECTION_LV2,
  "identity.person",
  "identity.birth",
  "identity.nationality",
  "identity.activity",
  "identity.residence",
  "identity.nif"
];

export const findRequiredConnectionKeysLv2 = (np: Partial<NaturalPerson>) => {
  const missingItems: RequiredConnectionKeysLv2 = {
    "identity.source_income": [],
    "identity.monthly_income": []
  };
  if (!np.sourcesIncome || np?.sourcesIncome.length === 0)
    missingItems["identity.source_income"].push("sourcesIncome");

  if (
    (np.minimumMonthlyIncome === null || np.minimumMonthlyIncome === undefined) &&
    (np.maximumMonthlyIncome === null || np.maximumMonthlyIncome === undefined)
  ) {
    missingItems["identity.monthly_income"].push("minimumMonthlyIncome");
    missingItems["identity.monthly_income"].push("maximumMonthlyIncome");
  }

  Object.keys(missingItems).forEach((key) => {
    if (missingItems[key as keyof RequiredConnectionKeysLv2].length === 0) {
      delete missingItems[key as keyof RequiredConnectionKeysLv2];
    }
  });

  return missingItems;
};

export const findRequiredConnectionKeys = (np: Partial<NaturalPerson>) => {
  const missingItemsLv2 = findRequiredConnectionKeysLv2(np);
  const missingItems: RequiredConnectionKeys = {
    ...missingItemsLv2,
    "identity.person": [],
    "identity.birth": [],
    "identity.nationality": [],
    "identity.activity": [],
    "identity.monthly_income": [],
    "identity.source_income": [],
    "phone.phone": [],
    "identity.residence": [],
    "identity.nif": []
  };
  if (!np.FirstName) missingItems["identity.person"].push("FirstName");
  if (!np.LastName) missingItems["identity.person"].push("LastName");
  if (!np.Gender) missingItems["identity.person"].push("Gender");
  if (!np.StateOfBirth) missingItems["identity.birth"].push("StateOfBirth");
  if (!np.countryOfBirth) missingItems["identity.birth"].push("countryOfBirth");
  if (!np.DateOfBirth) missingItems["identity.birth"].push("DateOfBirth");
  if (!np.CountryOfCitizenship) missingItems["identity.nationality"].push("CountryOfCitizenship");
  if (!np.CSPCode) {
    missingItems["identity.activity"].push("CSPCode");
    missingItems["identity.activity"].push("ActivityCode");
  } else if (!np.ActivityCode && !Object.values(cspWithoutActivity).includes(np.CSPCode)) {
    //ActivityCode is required only if CSPCode isn't chômeur, inactif, militaire, étudiant
    missingItems["identity.activity"].push("ActivityCode");
  }
  if (!np.AliasMobile || np.AliasMobile.length === 0)
    missingItems["phone.phone"].push("AliasMobile");
  if (!np.Address || np.Address.length === 0) missingItems["identity.residence"].push("Address");
  if (!np.TaxAddress || np.TaxAddress.length === 0) missingItems["identity.nif"].push("TaxAddress");

  Object.keys(missingItems).forEach((key) => {
    if (missingItems[key as keyof RequiredConnectionKeys].length === 0) {
      delete missingItems[key as keyof RequiredConnectionKeys];
    }
  });

  return missingItems;
};
export const findMissingProfileKeysSignup = (data: NaturalPerson) => {
  const structuredRequiredKeys: Record<string, string[]> = {
    "phone.phone": ["AliasMobile"],
    "identity.person": ["LastName", "FirstName", "Gender"],
    "identity.birth": ["countryOfBirth", "DateOfBirth", "StateOfBirth"],
    "identity.nationality": ["CountryOfCitizenship"],
    "identity.residence": ["Address"],
    "identity.nif": ["TaxAddress"],
    "identity.activity": ["CSPCode", "ActivityCode"],
    "identity.monthly_income": ["minimumMonthlyIncome", "maximumMonthlyIncome"],
    "identity.source_income": ["sourcesIncome"]
  };

  const missingItems: Record<string, string[]> = {};

  Object.keys(structuredRequiredKeys).forEach((section) => {
    const requiredFields = structuredRequiredKeys[section];
    missingItems[section] = [];

    if (section === "identity.activity") {
      if (!Object.values(cspWithoutActivity).includes(data.CSPCode) && !data.ActivityCode) {
        missingItems[section].push(...requiredFields); // Use spread operator to add all required fields
      }
    } else if (section === "identity.monthly_income") {
      if (
        (data.minimumMonthlyIncome === null || data.minimumMonthlyIncome === undefined) &&
        (data.maximumMonthlyIncome === null || data.maximumMonthlyIncome === undefined)
      ) {
        missingItems[section].push(...requiredFields); // Use spread operator to add all required fields
      }
    } else if (section === "identity.source_income") {
      if (!data.sourcesIncome || data.sourcesIncome.length === 0) {
        missingItems[section].push(...requiredFields); // Use spread operator to add all required fields
      }
    } else {
      requiredFields.forEach((key) => {
        if (key === "Address" || key === "AliasMobile" || key === "TaxAddress") {
          // Check if Address / AliasMobile has at least one element
          if (!Array.isArray(data[key]) || data[key].length === 0) {
            missingItems[section].push(key);
          }
        } else if (key === "DateOfBirth") {
          // Check if DateOfBirth has a date property
          if (!data[key] || !data[key].date) {
            missingItems[section].push(key);
          }
        } else {
          // Check if other keys are missing
          if (!data[key]) {
            missingItems[section].push(key);
          }
        }
      });
    }
  });
  // Remove keys with empty arrays
  Object.keys(missingItems).forEach((key) => {
    if (missingItems[key].length === 0) {
      delete missingItems[key]; // Remove key if its value is an empty array
    }
  });
  return missingItems;
};
